import type { FormSchemaV2, FormSection, PageBreak, ReportLayout } from '../../../lib/agentShared/formSchema';
// Explicit `.js` extension (unlike every type-only import above/elsewhere in
// this codebase) so this module's one VALUE dependency resolves under a
// plain Node ESM run too — see `scripts/report-studio-smoke.ts`'s own header
// comment for why that matters: it runs this exact reducer, unbundled, as
// this story's automated test for the typed-command/undo machinery.
import { reportLayoutOf, DEFAULT_REPORT_LAYOUT } from '../../../lib/agentShared/formSchema.js';
import type { ReportPresentation, SectionPresentation } from './reportPresentationSchema';
// Explicit `.ts` extension, same reasoning as the `formSchema.js` import
// above — resolvable both by tsc/Vite (`allowImportingTsExtensions`,
// `tsconfig.json`) and by a direct, unbundled Node run.
import { validateReportPresentation, defaultSectionPresentation, SectionPresentationPatchSchema } from './reportPresentationSchema.ts';

// Report Studio's typed-command/undo architecture (story 4-113). No
// undo/redo/dispatch pattern exists anywhere else in this frontend
// (`EditorCanvas.tsx` and the rest of `formBuilder/` mutate schema directly
// through `onChange`) — this is the first, and it is scoped to exactly what
// this story's own done-when requires: "UI controls dispatch typed commands
// rather than mutating nested objects directly, every command carrying undo
// metadata and validation... none directly mutate nested state."
//
// Domain: a `ReportStudioState` tuple of the EXISTING `FormSchemaV2` (whose
// `report: Partial<ReportLayout>` slice every Style/Content control already
// wrote to, pre-4-113, via ad hoc object spreads in `ReportDesigner.tsx`)
// plus the NEW `ReportPresentation` overlay (`reportPresentationSchema.ts`).
// Every mutation this story's UI performs is expressed as one of the
// commands below and applied through `applyReportStudioCommand`, the ONE
// place a report-studio mutation actually happens — call sites in
// `ReportDesigner.tsx`/`ReportStudioInspector.tsx` build a command object and
// dispatch it, they no longer spread `schema.report`/`presentation`
// themselves.
//
// Undo metadata is a pre-computed INVERSE command, captured from the state
// at apply time, not a blind full-object snapshot — that is what makes this
// a command-pattern reducer rather than a second copy of
// `TemplateEditorPage.tsx`'s own whole-schema undo stack (which stays
// exactly as it is, unaffected, for the Build/Preview tabs — see
// `useReportStudio.ts`'s own header comment for why the Report tab gets its
// own stack instead of sharing that one).

export interface ReportStudioState {
  schema: FormSchemaV2;
  presentation: ReportPresentation;
}

export type ReportStudioCommand =
  | { type: 'patchReportLayout'; label: string; payload: Partial<ReportLayout> }
  | { type: 'setStylePreset'; label: string; payload: { stylePresetId: string | null } }
  | {
    type: 'setSectionPresentation';
    label: string;
    payload: { sectionId: string; patch: Partial<SectionPresentation> };
  }
  // Story 4-114 — Document Tools' three template-section-level controls
  // ("hide a section from the report without removing it from capture",
  // "add page/section/column breaks... start on new page/keep with
  // previous", "reorder report sections independently from capture order").
  // These patch `schema.sections` directly (the SAME capture-schema field
  // `pageBreak` already lives on, `4-110`'s own precedent) rather than the
  // `presentation` overlay — `hidden`/`reportOrder` are report-OUTPUT
  // properties of a REAL capture-schema section, not a separate rendering
  // concern layered on top, exactly like `pageBreak` already is. Every
  // mutation still goes through this ONE dispatch path with a computed
  // inverse — "no direct state mutation" is about HOW the schema changes,
  // not WHICH field it touches.
  | { type: 'setSectionHidden'; label: string; payload: { sectionId: string; hidden: boolean } }
  | { type: 'setSectionPageBreak'; label: string; payload: { sectionId: string; pageBreak: PageBreak } }
  | { type: 'setSectionReportOrder'; label: string; payload: { orderedSectionIds: string[] } }
  // Story 4-116 — Section Presentation Tools' 8 new properties (`pageBreak`
  // above is the tool group's 9th — it already had its own command). ONE
  // generic command rather than 8 near-identical ones: every field lives in
  // the SAME `schema.sections[].presentation` object (mirroring
  // `setSectionHidden`/`setSectionPageBreak`'s own "patch `schema.sections`
  // directly" precedent, not the unused `presentation` overlay — see
  // `reportPresentationSchema.ts`'s own header comment on `SectionPresentation`
  // for why), so one patch-and-compute-inverse shape covers all of them.
  | {
    type: 'setSectionPresentationProperty';
    label: string;
    payload: { sectionId: string; patch: Partial<SectionPresentation> };
  }
  // Story 4-120 — Style Tools' three destructive reset actions. Per 4-113's
  // own interaction contract ("destructive reset actions require
  // confirmation and show their scope"), the UI (`ReportStudioPanels.tsx`/
  // `ReportDesigner.tsx`) shows a real confirm dialog stating the exact
  // scope BEFORE ever dispatching one of these — this reducer only knows how
  // to apply an already-confirmed command, same as every command above (no
  // control here mutates state directly, confirmed or not).
  //
  // `resetSectionReportFields` — scope: ONE section's report-OUTPUT fields
  // (`presentation`/`hidden`/`pageBreak` — never the section's capture
  // fields/answers). `restoreSectionReportFields` is its computed inverse
  // AND a symmetric, standalone command (applying it again computes a fresh
  // inverse from whatever is then-current) — the same "undo produces the
  // command that redoes it" shape every command above already has, just
  // carrying all three fields at once since a reset touches all three.
  | { type: 'resetSectionReportFields'; label: string; payload: { sectionId: string } }
  | {
    type: 'restoreSectionReportFields';
    label: string;
    payload: { sectionId: string; presentation: Partial<SectionPresentation>; hidden: boolean; pageBreak: PageBreak };
  }
  // `resetPageChrome` — scope: the physical page format + repeating header/
  // footer (`pageSize`/`pageOrientation`/`marginsPreset`/`coverPage`/
  // `content.tableOfContents`/`letterhead` — the Document/Header-Footer
  // tools' own fields, never the Style tab's color/typography/density
  // controls or the Content tab's toggles). Deliberately reuses
  // `patchReportLayout`'s OWN apply/inverse machinery (`patchReportLayout`/
  // `inverseLayoutPatch` below) rather than a new state shape — a reset IS a
  // patch to a fixed set of default values, so its inverse is the SAME
  // "capture prior value per touched key" computation every other
  // `patchReportLayout`-driven control already gets for free.
  | { type: 'resetPageChrome'; label: string; payload: Record<string, never> }
  // `resetReport` — scope: EVERYTHING `patchReportLayout` can reach (the
  // whole `ReportLayout` — Style/Layout/Header-Footer/Content/Media, all of
  // it) PLUS every section's own report-output fields (the same three
  // `resetSectionReportFields` resets, applied to every section at once).
  // Never touches `schema.sections[].fields`/capture answers/template
  // structure — same "reset/undo never modifies inspection answers or
  // capture structure" rule 4-113's own edge-case list states.
  // `restoreReportSnapshot` is its computed inverse AND a symmetric,
  // standalone command, same shape as `restoreSectionReportFields` above.
  | { type: 'resetReport'; label: string; payload: Record<string, never> }
  | {
    type: 'restoreReportSnapshot';
    label: string;
    payload: {
      layoutPatch: Partial<ReportLayout>;
      sections: Array<{ sectionId: string; presentation: Partial<SectionPresentation>; hidden: boolean; pageBreak: PageBreak }>;
    };
  };

function patchReportLayout(schema: FormSchemaV2, patch: Partial<ReportLayout>): FormSchemaV2 {
  const current = reportLayoutOf(schema);
  return {
    ...schema,
    report: {
      ...current,
      ...patch,
      // Same nested-merge `ReportDesigner.tsx`'s pre-4-113 `patchReport`
      // used for the one non-flat field (`content`) — kept here, in the
      // ONE reducer, instead of at every call site.
      ...(patch.content ? { content: { ...current.content, ...patch.content } } : {}),
    },
  };
}

/** The prior value of every key `patch` is about to touch — what running
 * `patchReportLayout` with THIS result undoes the original patch. Built
 * from the resolved layout (`reportLayoutOf`), so undoing a patch that was
 * applied against a schema with no `report` at all still resolves to the
 * real documented defaults, not `undefined`. */
function inverseLayoutPatch(schema: FormSchemaV2, patch: Partial<ReportLayout>): Partial<ReportLayout> {
  const current = reportLayoutOf(schema);
  const inverse: Partial<ReportLayout> = {};
  const inverseRecord = inverse as Record<string, unknown>;
  const currentRecord = current as unknown as Record<string, unknown>;
  for (const key of Object.keys(patch)) {
    inverseRecord[key] = key === 'content' ? { ...current.content } : currentRecord[key];
  }
  return inverse;
}

// Story 4-114 — the current REPORT order of every section (visible or
// hidden — reordering a hidden section still makes sense, it just has no
// visible effect until unhidden), independent of `schema.sections`' own
// (capture) array order: a section with an explicit `reportOrder` sorts by
// it, one without falls back to its own capture-order index — the SAME
// resolution `agents/shared/formSchema.js#orderedVisibleReportSections` uses
// for the real render, minus the "visible only" filter (this needs the full
// list to compute a coherent full reorder).
export function currentReportOrderIds(schema: FormSchemaV2): string[] {
  return schema.sections
    .map((section, index) => ({
      id: section.id,
      order: typeof section.reportOrder === 'number' ? section.reportOrder : index,
      index,
    }))
    .sort((a, b) => a.order - b.order || a.index - b.index)
    .map((entry) => entry.id);
}

/** Assigns `reportOrder` (0..n-1) to every section per its position in
 * `orderedSectionIds` — the array `schema.sections` ITSELF is never
 * reordered (that stays capture order forever), only this field changes. A
 * section id `orderedSectionIds` does not mention (should not happen — every
 * caller builds it from `currentReportOrderIds`, which always covers every
 * section) keeps its prior `reportOrder` untouched rather than being
 * silently dropped. */
function withReportOrder(schema: FormSchemaV2, orderedSectionIds: string[]): FormSchemaV2 {
  const orderIndex = new Map(orderedSectionIds.map((id, index) => [id, index]));
  return {
    ...schema,
    sections: schema.sections.map((section): FormSection =>
      orderIndex.has(section.id) ? { ...section, reportOrder: orderIndex.get(section.id)! } : section,
    ),
  };
}

function patchSchemaSection(schema: FormSchemaV2, sectionId: string, patch: Partial<FormSection>): FormSchemaV2 {
  return {
    ...schema,
    sections: schema.sections.map((section) => (section.id === sectionId ? { ...section, ...patch } : section)),
  };
}

// Story 4-116 — merges a validated patch into `section.presentation`, same
// "patch `schema.sections` directly" shape `setSectionHidden`/
// `setSectionPageBreak` already use (`patchSchemaSection` above), one level
// deeper. Missing fields on an untouched section resolve through
// `resolveSectionPresentation`'s own documented defaults at READ time (both
// renderers, and this file's own `currentSectionPresentation` below) —
// never written here just to fill them in.
function patchSectionPresentation(
  schema: FormSchemaV2,
  sectionId: string,
  patch: Partial<SectionPresentation>,
): FormSchemaV2 {
  return {
    ...schema,
    sections: schema.sections.map((section) =>
      section.id === sectionId
        ? { ...section, presentation: { ...(section.presentation ?? {}), ...patch } }
        : section,
    ),
  };
}

/** The section's CURRENT stored (possibly partial) presentation patch —
 * what a `setSectionPresentationProperty` command's inverse restores. Not
 * the fully-`resolveSectionPresentation`-resolved object: undoing a patch
 * that was applied against a section with no explicit value for a field
 * should return that field to "unset" (documented default at read time),
 * not freeze in whatever default happened to resolve at undo time. */
export function currentSectionPresentation(schema: FormSchemaV2, sectionId: string): Partial<SectionPresentation> {
  return schema.sections.find((section) => section.id === sectionId)?.presentation ?? {};
}

// Story 4-120 — the three section-scoped report-OUTPUT fields "Reset
// section" touches, resolved the SAME "missing key -> kind-aware default"
// way every renderer/resolver in this family already does (`pageBreak`'s own
// `?? (kind === 'page' ? 'always' : 'auto')`, `hidden`'s own `Boolean(...)`).
function currentSectionReportFields(
  schema: FormSchemaV2,
  sectionId: string,
): { presentation: Partial<SectionPresentation>; hidden: boolean; pageBreak: PageBreak } {
  const section = schema.sections.find((candidate) => candidate.id === sectionId);
  return {
    presentation: section?.presentation ?? {},
    hidden: Boolean(section?.hidden),
    pageBreak: section?.pageBreak ?? (section?.kind === 'page' ? 'always' : 'auto'),
  };
}

function withSectionReportFields(
  schema: FormSchemaV2,
  sectionId: string,
  fields: { presentation: Partial<SectionPresentation>; hidden: boolean; pageBreak: PageBreak },
): FormSchemaV2 {
  return {
    ...schema,
    sections: schema.sections.map((section) =>
      section.id === sectionId
        ? { ...section, presentation: fields.presentation, hidden: fields.hidden, pageBreak: fields.pageBreak }
        : section,
    ),
  };
}

/** The reset TARGET for "Reset section" — `presentation: {}` resolves every
 * property through its own documented default at read time (the same
 * "absent key" state a section that never touched Report Studio is already
 * in), `hidden: false`/`pageBreak` per-kind mirrors every other "byte-
 * identical to untouched" default this file already establishes. */
function defaultSectionReportFields(schema: FormSchemaV2, sectionId: string): {
  presentation: Partial<SectionPresentation>;
  hidden: boolean;
  pageBreak: PageBreak;
} {
  const section = schema.sections.find((candidate) => candidate.id === sectionId);
  return { presentation: {}, hidden: false, pageBreak: section?.kind === 'page' ? 'always' : 'auto' };
}

// Story 4-120 — "Reset page" scope: the physical page format + repeating
// header/footer fields only (see `ReportStudioCommand`'s own comment on
// `resetPageChrome` for the full field list and why each is included/
// excluded).
function pageChromeDefaultsPatch(current: ReportLayout): Partial<ReportLayout> {
  return {
    pageSize: DEFAULT_REPORT_LAYOUT.pageSize,
    pageOrientation: DEFAULT_REPORT_LAYOUT.pageOrientation,
    marginsPreset: DEFAULT_REPORT_LAYOUT.marginsPreset,
    coverPage: DEFAULT_REPORT_LAYOUT.coverPage,
    letterhead: DEFAULT_REPORT_LAYOUT.letterhead,
    content: { ...current.content, tableOfContents: DEFAULT_REPORT_LAYOUT.content.tableOfContents },
  };
}

export interface CommandResult {
  state: ReportStudioState;
  /** The command that undoes what was just applied. */
  inverse: ReportStudioCommand;
}

/**
 * The one place a `ReportStudioCommand` is actually applied. Every command
 * is validated against `reportPresentationSchema.ts` before it is accepted
 * into `presentation` — an invalid result (should not happen given the
 * payload shapes above, but the story's own done-when requires validation
 * on the command path, not just at load time) leaves `presentation`
 * unchanged rather than accepting corrupt state.
 */
export function applyReportStudioCommand(state: ReportStudioState, command: ReportStudioCommand): CommandResult {
  switch (command.type) {
    case 'patchReportLayout': {
      const inverse: ReportStudioCommand = {
        type: 'patchReportLayout',
        label: `Undo ${command.label}`,
        payload: inverseLayoutPatch(state.schema, command.payload),
      };
      return {
        state: { ...state, schema: patchReportLayout(state.schema, command.payload) },
        inverse,
      };
    }
    case 'setStylePreset': {
      const inverse: ReportStudioCommand = {
        type: 'setStylePreset',
        label: `Undo ${command.label}`,
        payload: { stylePresetId: state.presentation.stylePresetId ?? null },
      };
      const candidate: ReportPresentation = { ...state.presentation, stylePresetId: command.payload.stylePresetId };
      const validated = validateReportPresentation(candidate);
      return {
        state: { ...state, presentation: validated.ok ? validated.value : state.presentation },
        inverse,
      };
    }
    case 'setSectionPresentation': {
      const prior = state.presentation.sections[command.payload.sectionId] ?? defaultSectionPresentation();
      const inverse: ReportStudioCommand = {
        type: 'setSectionPresentation',
        label: `Undo ${command.label}`,
        payload: { sectionId: command.payload.sectionId, patch: prior },
      };
      const candidate: ReportPresentation = {
        ...state.presentation,
        sections: {
          ...state.presentation.sections,
          [command.payload.sectionId]: { ...prior, ...command.payload.patch },
        },
      };
      const validated = validateReportPresentation(candidate);
      return {
        state: { ...state, presentation: validated.ok ? validated.value : state.presentation },
        inverse,
      };
    }
    case 'setSectionHidden': {
      const priorSection = state.schema.sections.find((section) => section.id === command.payload.sectionId);
      const inverse: ReportStudioCommand = {
        type: 'setSectionHidden',
        label: `Undo ${command.label}`,
        payload: { sectionId: command.payload.sectionId, hidden: Boolean(priorSection?.hidden) },
      };
      return {
        state: { ...state, schema: patchSchemaSection(state.schema, command.payload.sectionId, { hidden: command.payload.hidden }) },
        inverse,
      };
    }
    case 'setSectionPageBreak': {
      const priorSection = state.schema.sections.find((section) => section.id === command.payload.sectionId);
      const inverse: ReportStudioCommand = {
        type: 'setSectionPageBreak',
        label: `Undo ${command.label}`,
        payload: { sectionId: command.payload.sectionId, pageBreak: priorSection?.pageBreak ?? 'auto' },
      };
      return {
        state: {
          ...state,
          schema: patchSchemaSection(state.schema, command.payload.sectionId, { pageBreak: command.payload.pageBreak }),
        },
        inverse,
      };
    }
    case 'setSectionPresentationProperty': {
      const prior = currentSectionPresentation(state.schema, command.payload.sectionId) as Record<string, unknown>;
      // Validated FIRST (before computing the inverse or applying anything)
      // — an invalid patch leaves state and history untouched, same "no
      // corrupt state accepted" rule `setSectionPresentation`'s overlay
      // command already applies via `validateReportPresentation` above.
      const validated = SectionPresentationPatchSchema.safeParse(command.payload.patch);
      if (!validated.success) {
        return {
          state,
          inverse: { type: 'setSectionPresentationProperty', label: `Undo ${command.label}`, payload: command.payload },
        };
      }
      const patch = validated.data as Partial<SectionPresentation>;
      const inversePatch: Partial<SectionPresentation> = {};
      const inverseRecord = inversePatch as Record<string, unknown>;
      for (const key of Object.keys(patch)) {
        inverseRecord[key] = prior[key];
      }
      const inverse: ReportStudioCommand = {
        type: 'setSectionPresentationProperty',
        label: `Undo ${command.label}`,
        payload: { sectionId: command.payload.sectionId, patch: inversePatch },
      };
      return {
        state: { ...state, schema: patchSectionPresentation(state.schema, command.payload.sectionId, patch) },
        inverse,
      };
    }
    case 'setSectionReportOrder': {
      const inverse: ReportStudioCommand = {
        type: 'setSectionReportOrder',
        label: `Undo ${command.label}`,
        payload: { orderedSectionIds: currentReportOrderIds(state.schema) },
      };
      return {
        state: { ...state, schema: withReportOrder(state.schema, command.payload.orderedSectionIds) },
        inverse,
      };
    }
    case 'resetSectionReportFields': {
      const prior = currentSectionReportFields(state.schema, command.payload.sectionId);
      const inverse: ReportStudioCommand = {
        type: 'restoreSectionReportFields',
        label: `Undo ${command.label}`,
        payload: { sectionId: command.payload.sectionId, ...prior },
      };
      const target = defaultSectionReportFields(state.schema, command.payload.sectionId);
      return {
        state: { ...state, schema: withSectionReportFields(state.schema, command.payload.sectionId, target) },
        inverse,
      };
    }
    case 'restoreSectionReportFields': {
      const prior = currentSectionReportFields(state.schema, command.payload.sectionId);
      const inverse: ReportStudioCommand = {
        type: 'restoreSectionReportFields',
        label: `Undo ${command.label}`,
        payload: { sectionId: command.payload.sectionId, ...prior },
      };
      return {
        state: {
          ...state,
          schema: withSectionReportFields(state.schema, command.payload.sectionId, {
            presentation: command.payload.presentation,
            hidden: command.payload.hidden,
            pageBreak: command.payload.pageBreak,
          }),
        },
        inverse,
      };
    }
    case 'resetPageChrome': {
      const patch = pageChromeDefaultsPatch(reportLayoutOf(state.schema));
      const inverse: ReportStudioCommand = {
        type: 'patchReportLayout',
        label: `Undo ${command.label}`,
        payload: inverseLayoutPatch(state.schema, patch),
      };
      return { state: { ...state, schema: patchReportLayout(state.schema, patch) }, inverse };
    }
    case 'resetReport': {
      // The prior FULL layout, captured via the same generic
      // `inverseLayoutPatch` every `patchReportLayout` command already uses
      // — passing `DEFAULT_REPORT_LAYOUT` as the "patch" whose keys are
      // about to change reads off the CURRENT value for every one of them,
      // i.e. the entire current layout, in one call.
      const priorLayoutPatch = inverseLayoutPatch(state.schema, DEFAULT_REPORT_LAYOUT);
      const priorSections = state.schema.sections.map((section) => ({
        sectionId: section.id,
        ...currentSectionReportFields(state.schema, section.id),
      }));
      const layoutResetSchema = patchReportLayout(state.schema, DEFAULT_REPORT_LAYOUT);
      const nextSchema: FormSchemaV2 = {
        ...layoutResetSchema,
        sections: layoutResetSchema.sections.map((section) => ({
          ...section,
          ...defaultSectionReportFields(layoutResetSchema, section.id),
        })),
      };
      const inverse: ReportStudioCommand = {
        type: 'restoreReportSnapshot',
        label: `Undo ${command.label}`,
        payload: { layoutPatch: priorLayoutPatch, sections: priorSections },
      };
      return { state: { ...state, schema: nextSchema }, inverse };
    }
    case 'restoreReportSnapshot': {
      const priorLayoutPatch = inverseLayoutPatch(state.schema, command.payload.layoutPatch);
      const priorSections = command.payload.sections.map(({ sectionId }) => ({
        sectionId,
        ...currentSectionReportFields(state.schema, sectionId),
      }));
      const layoutRestoredSchema = patchReportLayout(state.schema, command.payload.layoutPatch);
      const restoreBySectionId = new Map(command.payload.sections.map((entry) => [entry.sectionId, entry]));
      const nextSchema: FormSchemaV2 = {
        ...layoutRestoredSchema,
        sections: layoutRestoredSchema.sections.map((section) => {
          const restore = restoreBySectionId.get(section.id);
          return restore
            ? { ...section, presentation: restore.presentation, hidden: restore.hidden, pageBreak: restore.pageBreak }
            : section;
        }),
      };
      const inverse: ReportStudioCommand = {
        type: 'restoreReportSnapshot',
        label: `Undo ${command.label}`,
        payload: { layoutPatch: priorLayoutPatch, sections: priorSections },
      };
      return { state: { ...state, schema: nextSchema }, inverse };
    }
    default: {
      // Exhaustiveness check — a new command variant that forgets a case
      // here fails `tsc`, not silently falls through.
      const exhaustive: never = command;
      return { state, inverse: exhaustive };
    }
  }
}

export interface CommandStack {
  past: ReportStudioCommand[];
  future: ReportStudioCommand[];
}

export const EMPTY_COMMAND_STACK: CommandStack = { past: [], future: [] };
const MAX_HISTORY = 50;

export interface DispatchResult {
  state: ReportStudioState;
  stack: CommandStack;
}

/** Apply `command`, push its inverse onto `past`, clear `future` (the
 * standard "a new edit invalidates the redo branch" rule). */
export function dispatchReportStudioCommand(
  state: ReportStudioState,
  stack: CommandStack,
  command: ReportStudioCommand,
): DispatchResult {
  const { state: nextState, inverse } = applyReportStudioCommand(state, command);
  return {
    state: nextState,
    stack: { past: [...stack.past.slice(-(MAX_HISTORY - 1)), inverse], future: [] },
  };
}

/** Pop the last inverse off `past`, apply it (which itself produces the
 * command that redoes the original edit), push that onto `future`. */
export function undoReportStudio(state: ReportStudioState, stack: CommandStack): DispatchResult {
  const inverse = stack.past[stack.past.length - 1];
  if (!inverse) return { state, stack };
  const { state: nextState, inverse: redoCommand } = applyReportStudioCommand(state, inverse);
  return {
    state: nextState,
    stack: { past: stack.past.slice(0, -1), future: [...stack.future, redoCommand] },
  };
}

/** Mirror of `undoReportStudio` over `future`. */
export function redoReportStudio(state: ReportStudioState, stack: CommandStack): DispatchResult {
  const redoCommand = stack.future[stack.future.length - 1];
  if (!redoCommand) return { state, stack };
  const { state: nextState, inverse } = applyReportStudioCommand(state, redoCommand);
  return {
    state: nextState,
    stack: { past: [...stack.past, inverse], future: stack.future.slice(0, -1) },
  };
}
